import { useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { QueryBuilderMaterial } from "@react-querybuilder/material";
import {
  QueryBuilder,
  RuleComponents,
  RuleGroupBodyComponents,
  type ActionProps,
  type CombinatorSelectorProps,
  type Field,
  type FieldSelectorProps,
  type OperatorSelectorProps,
  type QueryValidator,
  type RuleGroupProps,
  type RuleGroupType,
  type RuleProps,
  type UseRuleGroup,
  type ValueEditorProps,
  useRule,
} from "react-querybuilder";
import { useNavigate } from "react-router-dom";
import "react-querybuilder/dist/query-builder.css";
import "./EnhancedTaggingRuleConditionBuilder.css";
import { buildAiPlanPath } from "../constants";
import { useConfigurableConcepts } from "../../../hooks/useConfigurableConcepts";
import ConceptValuesMultiSelect from "../TaggingPlan/ConceptValuesMultiSelect";
import {
  FIELD_PLACEHOLDER_LABEL,
  FIELD_PLACEHOLDER_NAME,
  createEmptyConditionValue,
  isFieldPlaceholderValue,
  parseEnhancedTaggingConditionValue,
  type EnhancedTaggingConditionValue,
} from "./enhancedTaggingConditionValue";
import {
  countEnhancedTaggingConditionRules,
  hasValidationReason,
  validateEnhancedTaggingConditionQuery,
} from "./validateEnhancedTaggingCondition";

export type { EnhancedTaggingConditionValue };
export {
  createEmptyConditionValue,
  isFieldPlaceholderValue,
  parseEnhancedTaggingConditionValue,
};

/** Match Concept Values / Tagging Plan outlined selects; keep soft gray border. */
const conditionSelectFormSx: SxProps<Theme> = {
  width: "100%",
  "& .MuiInputBase-root": {
    height: 40,
    borderRadius: "6px",
    bgcolor: "#fff",
    fontSize: "0.875rem",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E5E7EB",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E5E7EB",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E5E7EB",
    borderWidth: 1,
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    py: 0,
  },
};

export const createEmptyConditionQuery = (): RuleGroupType => ({
  combinator: "or",
  rules: [
    {
      combinator: "and",
      rules: [
        {
          // Empty string (not FIELD_PLACEHOLDER_NAME) so RQB still shows
          // operator + value controls alongside the field selector.
          field: "",
          operator: "containsAny",
          value: createEmptyConditionValue(),
        },
      ],
    },
  ],
});

const OPERATORS = [
  { name: "containsAny", label: "containsAny" },
  { name: "containsAll", label: "containsAll" },
  { name: "doesNotContainAny", label: "doesNotContainAny" },
];

function capitalizeDisplayLabel(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function ConceptFieldSelector({
  className,
  handleOnChange,
  options,
  value,
  disabled,
  title,
  validation,
}: FieldSelectorProps) {
  const flatOptions = useMemo(() => {
    const result: Array<{ name: string; label: string }> = [];
    for (const option of options) {
      if ("options" in option && Array.isArray(option.options)) {
        for (const nested of option.options) {
          const name = String(
            "name" in nested ? nested.name : (nested as { value?: string }).value ?? "",
          );
          const label = String(
            "label" in nested ? nested.label : name,
          );
          result.push({ name, label });
        }
      } else {
        const name = String(
          "name" in option ? option.name : (option as { value?: string }).value ?? "",
        );
        const label = String("label" in option ? option.label : name);
        result.push({ name, label });
      }
    }
    return result.filter((opt) => !isFieldPlaceholderValue(opt.name));
  }, [options]);

  const showError = hasValidationReason(validation, "field");

  return (
    <FormControl
      size="small"
      fullWidth
      disabled={disabled}
      className={className}
      title={title}
      error={showError}
      sx={conditionSelectFormSx}
    >
      <Select
        displayEmpty
        error={showError}
        value={isFieldPlaceholderValue(value) ? "" : value}
        onChange={(event) => {
          // Keep empty string (not "~") so operator/value stay visible.
          handleOnChange(String(event.target.value ?? ""));
        }}
        renderValue={(selected) => {
          if (!selected || isFieldPlaceholderValue(selected)) {
            return (
              <Box component="span" sx={{ color: "#9ca3af" }}>
                {FIELD_PLACEHOLDER_LABEL}
              </Box>
            );
          }
          const matched = flatOptions.find((opt) => opt.name === selected);
          return matched?.label ?? selected;
        }}
      >
        <MenuItem value="">
          <Box component="span" sx={{ color: "#9ca3af" }}>
            {FIELD_PLACEHOLDER_LABEL}
          </Box>
        </MenuItem>
        {flatOptions.map((opt) => (
          <MenuItem key={opt.name} value={opt.name}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function ConceptOperatorSelector({
  className,
  handleOnChange,
  options,
  value,
  disabled,
  title,
  validation,
}: OperatorSelectorProps) {
  const flatOptions = useMemo(() => {
    const result: Array<{ name: string; label: string }> = [];
    for (const option of options) {
      if ("options" in option && Array.isArray(option.options)) {
        for (const nested of option.options) {
          const name = String(
            "name" in nested ? nested.name : (nested as { value?: string }).value ?? "",
          );
          const label = String(
            "label" in nested ? nested.label : name,
          );
          result.push({ name, label });
        }
      } else {
        const name = String(
          "name" in option ? option.name : (option as { value?: string }).value ?? "",
        );
        const label = String("label" in option ? option.label : name);
        result.push({ name, label });
      }
    }
    return result;
  }, [options]);

  const showError = hasValidationReason(validation, "operator");

  return (
    <FormControl
      size="small"
      fullWidth
      disabled={disabled}
      className={className}
      title={title}
      error={showError}
      sx={conditionSelectFormSx}
    >
      <Select
        displayEmpty
        error={showError}
        value={value ?? ""}
        onChange={(event) => handleOnChange(String(event.target.value ?? ""))}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Box component="span" sx={{ color: "#9ca3af" }}>
                Please select...
              </Box>
            );
          }
          const matched = flatOptions.find((opt) => opt.name === selected);
          return matched?.label ?? selected;
        }}
      >
        {flatOptions.map((opt) => (
          <MenuItem key={opt.name} value={opt.name}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function IconTrashAction({
  handleOnClick,
  title,
  disabled,
  className,
}: ActionProps) {
  return (
    <IconButton
      size="small"
      title={title}
      disabled={disabled}
      className={`etr-icon-action ${className ?? ""}`}
      onClick={(event) => handleOnClick(event)}
      aria-label={title}
    >
      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
    </IconButton>
  );
}

function AddConditionAction({
  handleOnClick,
  label,
  title,
  disabled,
  className,
}: ActionProps) {
  return (
    <Button
      type="button"
      disableRipple
      disabled={disabled}
      title={title}
      className={`etr-add-condition ${className ?? ""}`}
      onClick={(event) => handleOnClick(event)}
    >
      {label}
    </Button>
  );
}

function AddGroupAction({
  handleOnClick,
  label,
  title,
  disabled,
  className,
}: ActionProps) {
  return (
    <Button
      type="button"
      variant="outlined"
      fullWidth
      disabled={disabled}
      title={title}
      className={`etr-add-group ${className ?? ""}`}
      onClick={(event) => handleOnClick(event)}
    >
      {label}
    </Button>
  );
}

function ConditionRule(props: RuleProps) {
  const rule = useRule(props);
  const ruleIndex = props.path[props.path.length - 1] ?? 0;
  const connectorLabel = ruleIndex === 0 ? "when" : "and";

  return (
    <div className={`${rule.outerClassName} etr-rule-vertical`}>
      <span className="etr-when-label">{connectorLabel}</span>
      <RuleComponents {...rule} />
    </div>
  );
}

function ConditionGroupHeader(props: RuleGroupProps & UseRuleGroup) {
  const {
    path,
    schema,
    disabled,
    removeGroup,
    classNames,
    translations,
  } = props;

  // Root: add-group lives below the body (see ConditionGroupBody)
  if (path.length === 0) {
    return null;
  }

  // Nested condition card header
  const groupIndex = (path[path.length - 1] ?? 0) + 1;
  const RemoveGroup = schema.controls.removeGroupAction;

  return (
    <>
      <span className="etr-group-title">Condition group {groupIndex}</span>
      {RemoveGroup ? (
        <RemoveGroup
          testID="remove-group"
          label={translations.removeGroup.label}
          title={translations.removeGroup.title}
          className={classNames.removeGroup}
          handleOnClick={removeGroup}
          rules={props.ruleGroup.rules}
          level={path.length}
          path={path}
          disabled={disabled}
          context={props.context}
          validation={props.validationResult}
          ruleOrGroup={props.ruleGroup}
          schema={schema}
        />
      ) : null}
    </>
  );
}

function ConditionGroupBody(props: RuleGroupProps & UseRuleGroup) {
  const {
    path,
    schema,
    disabled,
    addRule,
    addGroup,
    classNames,
    translations,
  } = props;
  const AddRule = schema.controls.addRuleAction;
  const AddGroup = schema.controls.addGroupAction;

  return (
    <>
      <RuleGroupBodyComponents {...props} />
      {path.length > 0 && AddRule ? (
        <AddRule
          testID="add-rule"
          label={translations.addRule.label}
          title={translations.addRule.title}
          className={classNames.addRule}
          handleOnClick={addRule}
          rules={props.ruleGroup.rules}
          level={path.length}
          path={path}
          disabled={disabled}
          context={props.context}
          validation={props.validationResult}
          ruleOrGroup={props.ruleGroup}
          schema={schema}
        />
      ) : null}
      {path.length === 0 && AddGroup ? (
        <AddGroup
          testID="add-group"
          label={translations.addGroup.label}
          title={translations.addGroup.title}
          className={classNames.addGroup}
          handleOnClick={addGroup}
          rules={props.ruleGroup.rules}
          level={path.length}
          path={path}
          disabled={disabled}
          context={props.context}
          validation={props.validationResult}
          ruleOrGroup={props.ruleGroup}
          schema={schema}
        />
      ) : null}
    </>
  );
}

function OrMeetsCombinator({
  className,
  ruleGroup,
}: CombinatorSelectorProps) {
  // Only show between root-level condition groups (OR). Nested AND groups stay implicit.
  if (!("combinator" in ruleGroup) || ruleGroup.combinator !== "or") {
    return null;
  }
  return <span className={`etr-or-meets ${className ?? ""}`}>Or meets</span>;
}

function ConceptValueEditor(props: ValueEditorProps) {
  const {
    field,
    fieldData,
    handleOnChange,
    value,
    disabled,
    operator,
    validation,
  } = props;

  if (operator === "null" || operator === "notNull") {
    return null;
  }

  const parsed = parseEnhancedTaggingConditionValue(value);
  const conceptName =
    typeof fieldData?.label === "string" ? fieldData.label : undefined;
  const conceptCode =
    !field || isFieldPlaceholderValue(String(field)) ? "" : String(field);
  const showError = hasValidationReason(validation, "value");

  return (
    <Box className="etr-value-editor" sx={{ width: "100%", minWidth: 0 }}>
      <ConceptValuesMultiSelect
        hideLabel
        showAllOption={false}
        conceptCode={conceptCode}
        conceptName={conceptName}
        value={parsed.conceptValues}
        allValues={false}
        disabled={disabled || !conceptCode}
        error={showError}
        placeholder="Please select..."
        onChange={(next) => {
          handleOnChange({
            allValues: false,
            conceptValues: next.conceptValues,
          } satisfies EnhancedTaggingConditionValue);
        }}
      />
    </Box>
  );
}

type EnhancedTaggingRuleConditionBuilderProps = Readonly<{
  query: RuleGroupType;
  onQueryChange: (query: RuleGroupType) => void;
  /** API `target_type` used to load condition concept fields. */
  targetType: string;
  showValidation?: boolean;
}>;

export default function EnhancedTaggingRuleConditionBuilder({
  query,
  onQueryChange,
  targetType,
  showValidation = false,
}: EnhancedTaggingRuleConditionBuilderProps) {
  const navigate = useNavigate();
  const { conceptOptions, loading: conceptsLoading } =
    useConfigurableConcepts(targetType);

  const fields = useMemo<Field[]>(
    () =>
      conceptOptions.map((opt) => ({
        name: opt.code,
        label: capitalizeDisplayLabel(opt.name || opt.code),
        valueEditorType: "select",
      })),
    [conceptOptions],
  );

  const ruleCount = useMemo(
    () => countEnhancedTaggingConditionRules(query),
    [query],
  );

  const validator = useCallback<QueryValidator>(
    (nextQuery) => {
      if (!showValidation) return true;
      const result = validateEnhancedTaggingConditionQuery(
        nextQuery as RuleGroupType,
      );
      if (result.ruleCount === 0) return false;
      return result.validationMap;
    },
    [showValidation],
  );

  const GuardedRemoveAction = useCallback(
    (props: ActionProps) => (
      <IconTrashAction
        {...props}
        disabled={props.disabled || ruleCount <= 1}
      />
    ),
    [ruleCount],
  );

  return (
    <Box className="etr-query-builder">
      <Typography sx={{ fontSize: "14px", color: "#595959", mb: 2, lineHeight: 1.5 }}>
        Configure concepts and values in the{" "}
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => navigate(buildAiPlanPath("taggingPlan"))}
          sx={{
            color: "#295EF1",
            fontSize: "14px",
            fontWeight: 500,
            verticalAlign: "baseline",
            cursor: "pointer",
          }}
        >
          Tagging Plan
        </Link>
        , then select them here to define enhanced tagging plan.
      </Typography>

      <QueryBuilderMaterial>
        <QueryBuilder
          fields={fields}
          query={query}
          onQueryChange={onQueryChange}
          operators={OPERATORS}
          disabled={conceptsLoading}
          showCombinatorsBetweenRules
          addRuleToNewGroups
          getDefaultValue={() => createEmptyConditionValue()}
          autoSelectField={false}
          validator={validator}
          combinators={[
            { name: "and", label: "And" },
            { name: "or", label: "Or meets" },
          ]}
          getRuleGroupClassname={(rg) =>
            rg.combinator === "or" ? "etr-root-group" : "etr-condition-group"
          }
          translations={{
            fields: {
              title: "Dimension",
              placeholderName: FIELD_PLACEHOLDER_NAME,
              placeholderLabel: FIELD_PLACEHOLDER_LABEL,
            },
            operators: { title: "Operator" },
            values: { title: "Value" },
            value: { title: "Value" },
            removeRule: { label: "Remove condition", title: "Remove condition" },
            removeGroup: { label: "Remove group", title: "Remove group" },
            addRule: { label: "+ Add condition", title: "Add condition" },
            addGroup: {
              label: "+ Add condition group",
              title: "Add condition group",
            },
          }}
          controlElements={{
            rule: ConditionRule,
            ruleGroupHeaderElements: ConditionGroupHeader,
            ruleGroupBodyElements: ConditionGroupBody,
            fieldSelector: ConceptFieldSelector,
            operatorSelector: ConceptOperatorSelector,
            valueEditor: ConceptValueEditor,
            combinatorSelector: OrMeetsCombinator,
            addRuleAction: AddConditionAction,
            addGroupAction: AddGroupAction,
            removeRuleAction: GuardedRemoveAction,
            removeGroupAction: GuardedRemoveAction,
            cloneRuleAction: null,
            cloneGroupAction: null,
            lockRuleAction: null,
            lockGroupAction: null,
            notToggle: null,
            dragHandle: null,
          }}
        />
      </QueryBuilderMaterial>
    </Box>
  );
}
